
/**
 * Module dependencies.
 */

import { CSSTransition, TransitionGroup } from 'react-transition-group';
import { ReactNode, useCallback, useMemo, useReducer } from 'react';
import { SnackbarContext } from './snackbar-context';
import { SnackbarMessage, SnackbarMessageOptions } from './snackbar-message';
import { theme } from 'styled-tools';
import styled from 'styled-components';

/**
 * `State` type.
 */

type State = {
  messages: Array<{
    content: ReactNode;
    id: number;
    options?: SnackbarMessageOptions;
  }>;
  total: number;
};

/**
 * `Props` type.
 */

type Props = {
  children: ReactNode;
};

/**
 * Action types.
 */

const actionTypes = {
  addMessage: 'ADD_MESSAGE',
  removeMessage: 'REMOVE_MESSAGE'
};

/**
 * Reducer.
 */

function reducer(state: State, action: any) {
  switch (action.type) {
    case actionTypes.addMessage: {
      const total = state.total + 1;
      const messages = state.messages.filter(({ content }) => {
        return content !== action.payload?.message?.content;
      });

      return {
        messages: [
          ...messages,
          {
            ...action.payload?.message,
            id: total
          }
        ],
        total
      };
    }

    case actionTypes.removeMessage:
      return {
        messages: state.messages.filter(({ id }) => id !== action.payload.id),
        total: state.total - 1
      };

    default:
      return state;
  }
}

/**
 * Snackbar transition time.
 */

const snackbarTransitionTime = 255;

/**
 * `Wrapper` styled component.
 */

const Wrapper = styled.div`
  left: 8px;
  margin: 0 auto;
  max-width: ${theme('dimensions.snackbar.maxWidth')}px;
  position: fixed;
  right: 8px;
  top: 8px;
  z-index: ${theme('zIndex.snackbar')};

  .snackbar-messages-item-enter {
    opacity: 0;
    transform: translateY(8px);
  }

  .snackbar-messages-item-enter-active {
    opacity: 1;
    transform: translateY(0);
    transition:
      opacity ${snackbarTransitionTime}ms ${theme('animations.bezier.easeInOutQuad')},
      transform 0.15s ${theme('animations.bezier.easeInOutQuad')};
  }

  .snackbar-messages-item-exit {
    opacity: 1;
  }

  .snackbar-messages-item-exit-active {
    opacity: 0;
    transition: opacity ${snackbarTransitionTime}ms ${theme('animations.bezier.easeInOutQuad')};
  }
`;

/**
 * Export `SnackbarProvider` component.
 */

export function SnackbarProvider({ children }: Props): JSX.Element {
  const [{ messages }, dispatch] = useReducer(reducer, { messages: [], total: 0 });
  const showMessage = useCallback((content: any, options: any) => {
    dispatch({
      payload: {
        message: { content, options }
      },
      type: actionTypes.addMessage
    });
  }, []);

  const snackbarActions = useMemo(() => ({
    showErrorMessage: (content: any, options: any) => {
      showMessage(content, { ...options, type: 'error' });
    },
    showInfoMessage: (content: any, options: any) => {
      showMessage(content, { ...options, type: 'info' });
    },
    showSuccessMessage: (content: any, options: any) => {
      showMessage(content, { ...options, type: 'success' });
    },
    showWarningMessage: (content: any, options: any) => {
      showMessage(content, { ...options, type: 'warning' });
    }
  }), [showMessage]);

  const removeMessage = useCallback((id: number) => {
    dispatch({
      payload: { id },
      type: actionTypes.removeMessage
    });
  }, []);

  return (
    <SnackbarContext.Provider value={snackbarActions}>
      {children}

      <Wrapper>
        <TransitionGroup>
          {messages.map(({ content, id, options }) => (
            <CSSTransition
              classNames={'snackbar-messages-item'}
              key={id}
              timeout={snackbarTransitionTime}
            >
              <SnackbarMessage
                id={id}
                onRemove={removeMessage}
                options={options}
              >
                {content}
              </SnackbarMessage>
            </CSSTransition>
          ))}
        </TransitionGroup>
      </Wrapper>
    </SnackbarContext.Provider>
  );
}
